import { useApolloClient } from '@vue/apollo-composable';
import { ref } from 'vue';
import { useRouter } from 'vue-router';

import type { Location } from '@/entities/auth';

import { useAuthStore } from '@/entities/auth';
import { findRememberedOpenLocation } from '@/entities/location';
import { useToast } from '@/shared/lib';
import { usePageReload } from '@/shared/lib/composables';
import { extractApiErrorMessage } from '@/shared/lib/helpers';

import { useConfirmLeavingKassa } from './confirm-leaving-kassa';
import { useLocationSwitch } from './use-location-switch';

export const LOCATION_SWITCH_ERROR = 'Ошибка при переключении локации';

/**
 * Выбор текущей локации приложения вместе с финализацией Apollo.
 *
 * Живёт здесь, а не в шапке: склад выбирает ту же самую текущую локацию, и
 * второй набор правил переключения означал бы вторую «текущую локацию» с
 * собственным состоянием кэша.
 */
export function useLocationSelection() {
  const authStore = useAuthStore();
  const { switchLocation } = useLocationSwitch();
  const toast = useToast();
  const { resolveClient } = useApolloClient();
  const router = useRouter();
  const { confirmLeavingKassa } = useConfirmLeavingKassa();
  const { reloadCurrentPage } = usePageReload();

  const isLoading = ref(false);

  const resetApolloStore = async (): Promise<void> => {
    try {
      await resolveClient().resetStore();
    } catch (cacheError) {
      console.warn('[useLocationSelection] Apollo resetStore failed after location switch', { error: cacheError });
      await resolveClient().clearStore();
    }
  };

  /** Возвращает, состоялось ли переключение. */
  const selectLocation = async (location: Location): Promise<boolean> => {
    const organizationId = authStore.currentOrganization?.id;

    if (!organizationId || isLoading.value) {
      return false;
    }

    if (location.id === authStore.currentLocation?.id) {
      return true;
    }

    if (!(await confirmLeavingKassa({ organizationId, locationId: location.id, title: location.title }))) {
      return false;
    }

    isLoading.value = true;

    try {
      const switchResult = await switchLocation(organizationId, location.id);

      if (switchResult.status === 'failure') {
        throw new Error('Не удалось переключить локацию');
      }

      // Страница пересоздаётся на пустом кэше: её запросы сразу уходят в новую локацию, без кадра
      // со старыми данными. Если страница держит черновик и отказала, она остаётся и перезапрашивается.
      await resolveClient().clearStore();
      if (!(await reloadCurrentPage())) {
        await resetApolloStore();
      }

      return true;
    } catch (error: unknown) {
      toast.error(extractApiErrorMessage(error, LOCATION_SWITCH_ERROR));

      return false;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Смена организации: открывается последняя локация в ней, иначе — выбор локации.
   * После смены — домашняя страница новой организации: адрес прежней (заказ, клиент, раздел
   * без модуля) в ней не существует или недоступен.
   */
  const selectOrganization = async (organizationId: string): Promise<void> => {
    if (isLoading.value || organizationId === authStore.currentOrganization?.id) {
      return;
    }

    const organization = authStore.availableOrganizations.find((item) => item.id === organizationId);
    if (!(await confirmLeavingKassa({ organizationId, locationId: null, title: organization?.title ?? null }))) {
      return;
    }

    const remembered = findRememberedOpenLocation(organizationId, organization?.locations);
    if (!remembered) {
      await router.push({ name: 'auth.location-switch', query: { organization: organizationId } });
      return;
    }

    isLoading.value = true;
    try {
      const switchResult = await switchLocation(organizationId, remembered.id);
      if (switchResult.status === 'failure') {
        await router.push({ name: 'auth.location-switch', query: { organization: organizationId } });
        return;
      }
      // Сначала уходим со страницы прежней организации, чтобы перезапрос кэша не бил её запросами.
      await router.push({ name: 'home.index' });
      await resetApolloStore();
    } finally {
      isLoading.value = false;
    }
  };

  return { isLoading, selectLocation, selectOrganization };
}
